import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Star, Users, Clock, Award } from 'lucide-react';
import { Code, Cloud, Smartphone, Database, CircuitBoard, Zap } from 'lucide-react';

const ServiceDetail = () => {
  const { serviceId } = useParams();

  const serviceData: Record<string, any> = {
    'Enterprise Software Development': {
      icon: Code,
      title: 'Enterprise Software Development',
      subtitle: 'Tailored Solutions for Your Unique Business Needs',
      description: 'Transform your business with custom software solutions designed specifically for your requirements. Our expert development team creates scalable, secure, and user-friendly applications that drive growth and efficiency.',
      image: 'https://images.pexels.com/photos/3861958/pexels-photo-3861958.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Custom Web Applications',
        'Desktop Software Solutions',
        'API Development & Integration',
        'Legacy System Modernization',
        'Database Design & Optimization',
        'Third-party Integrations'
      ],
      benefits: [
        'Increased operational efficiency by 40%',
        'Reduced manual processes and errors',
        'Scalable architecture for future growth',
        'Enhanced user experience and satisfaction',
        'Improved data security and compliance',
        'Cost-effective long-term solution'
      ],
      technologies: ['React', 'Node.js', 'Python', 'Java', 'PostgreSQL', 'MongoDB', 'AWS', 'Docker'],
      process: [
        { step: 'Discovery & Analysis', description: 'Understanding your business requirements and technical needs' },
        { step: 'Design & Architecture', description: 'Creating scalable system architecture and user-friendly designs' },
        { step: 'Development & Testing', description: 'Agile development with continuous testing and quality assurance' },
        { step: 'Deployment & Support', description: 'Seamless deployment with ongoing maintenance and support' }
      ],
      caseStudies: [
        {
          title: 'E-commerce Platform Transformation',
          client: 'RetailCorp',
          result: '300% increase in online sales',
          description: 'Built a comprehensive e-commerce platform with advanced inventory management and analytics.'
        },
        {
          title: 'Healthcare Management System',
          client: 'MedTech Solutions',
          result: '50% reduction in administrative time',
          description: 'Developed an integrated healthcare platform connecting patients, doctors, and administrators.'
        }
      ]
    },
    'cloud-solutions': {
      icon: Cloud,
      title: 'Cloud Solutions',
      subtitle: 'Scalable Infrastructure for Modern Businesses',
      description: 'Leverage the power of cloud computing to scale your business efficiently. Our cloud solutions provide flexibility, security, and cost-effectiveness while ensuring high availability and performance.',
      image: 'https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Cloud Migration Services',
        'AWS & Azure Architecture',
        'DevOps Implementation',
        'Serverless Solutions',
        'Container Orchestration',
        'Cloud Security & Compliance'
      ],
      benefits: [
        'Reduced infrastructure costs by 60%',
        'Improved scalability and flexibility',
        'Enhanced disaster recovery capabilities',
        'Increased system reliability and uptime',
        'Faster deployment and time-to-market',
        'Global accessibility and performance'
      ],
      technologies: ['AWS', 'Azure', 'Google Cloud', 'Kubernetes', 'Docker', 'Terraform', 'Jenkins', 'Ansible'],
      process: [
        { step: 'Cloud Assessment', description: 'Evaluating current infrastructure and cloud readiness' },
        { step: 'Migration Strategy', description: 'Developing a comprehensive migration plan and timeline' },
        { step: 'Implementation', description: 'Executing migration with minimal downtime and disruption' },
        { step: 'Optimization', description: 'Continuous monitoring and optimization for performance and cost' }
      ],
      caseStudies: [
        {
          title: 'Enterprise Cloud Migration',
          client: 'TechCorp Industries',
          result: '70% cost reduction in IT infrastructure',
          description: 'Migrated legacy systems to AWS with improved performance and scalability.'
        },
        {
          title: 'Multi-Cloud Strategy Implementation',
          client: 'Global Finance Ltd',
          result: '99.99% uptime achievement',
          description: 'Implemented a robust multi-cloud architecture for high availability and disaster recovery.'
        }
      ]
    },
    'Web & Mobile App Development': {
      icon: Smartphone,
      title: 'Mobile App Development',
      subtitle: 'Engaging Mobile Experiences for iOS and Android',
      description: 'Create powerful mobile applications that engage users and drive business growth. Our mobile development expertise spans native iOS and Android apps, as well as cross-platform solutions.',
      image: 'https://images.pexels.com/photos/607812/pexels-photo-607812.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Native iOS Development',
        'Native Android Development',
        'Cross-platform Solutions',
        'UI/UX Design',
        'App Store Optimization',
        'Mobile Backend Services'
      ],
      benefits: [
        'Increased user engagement by 80%',
        'Enhanced brand visibility and reach',
        'Improved customer satisfaction',
        'New revenue streams and opportunities',
        'Real-time customer insights',
        'Competitive market advantage'
      ],
      technologies: ['Swift', 'Kotlin', 'React Native', 'Flutter', 'Xamarin', 'Firebase', 'GraphQL', 'REST APIs'],
      process: [
        { step: 'Concept & Strategy', description: 'Defining app concept, target audience, and market strategy' },
        { step: 'Design & Prototyping', description: 'Creating intuitive designs and interactive prototypes' },
        { step: 'Development & Testing', description: 'Building robust apps with comprehensive testing across devices' },
        { step: 'Launch & Marketing', description: 'App store submission and marketing strategy implementation' }
      ],
      caseStudies: [
        {
          title: 'Fitness Tracking App',
          client: 'HealthTech Startup',
          result: '1M+ downloads in first year',
          description: 'Developed a comprehensive fitness app with social features and AI-powered recommendations.'
        },
        {
          title: 'Banking Mobile App',
          client: 'Regional Bank',
          result: '90% customer adoption rate',
          description: 'Created a secure mobile banking app with advanced features and biometric authentication.'
        }
      ]
    },
    'Agentic AI & Gen AI': {
      icon: Database,
      title: 'Data Analytics & AI',
      subtitle: 'Transform Data into Actionable Business Insights',
      description: 'Unlock the power of your data with advanced analytics and artificial intelligence. Our solutions help you make data-driven decisions, predict trends, and automate processes for competitive advantage.',
      image: 'https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Business Intelligence Dashboards',
        'Machine Learning Models',
        'Predictive Analytics',
        'Data Visualization',
        'Natural Language Processing',
        'Computer Vision Solutions'
      ],
      benefits: [
        'Improved decision-making accuracy by 75%',
        'Automated routine processes',
        'Enhanced customer insights and targeting',
        'Reduced operational costs',
        'Faster time-to-insight',
        'Competitive market intelligence'
      ],
      technologies: ['Python', 'R', 'TensorFlow', 'PyTorch', 'Apache Spark', 'Tableau', 'Power BI', 'Elasticsearch'],
      process: [
        { step: 'Data Assessment', description: 'Evaluating data quality, sources, and analytics requirements' },
        { step: 'Model Development', description: 'Building and training machine learning models for your use case' },
        { step: 'Integration & Deployment', description: 'Integrating AI solutions into your existing systems' },
        { step: 'Monitoring & Optimization', description: 'Continuous model monitoring and performance optimization' }
      ],
      caseStudies: [
        {
          title: 'Predictive Maintenance System',
          client: 'Manufacturing Corp',
          result: '40% reduction in equipment downtime',
          description: 'Implemented AI-powered predictive maintenance to prevent equipment failures.'
        },
        {
          title: 'Customer Behavior Analytics',
          client: 'E-commerce Giant',
          result: '25% increase in conversion rates',
          description: 'Developed ML models for personalized product recommendations and customer segmentation.'
        }
      ]
    },
    'IoT Solutions': {
      icon: CircuitBoard,
      title: 'IoT Solutions',
      subtitle: 'Comprehensive Security Solutions for Digital Assets',
      description: 'Unlock the power of connected devices with our comprehensive IoT solutions. We design, deploy, and manage scalable IoT infrastructures to enhance automation, visibility, and efficiency across your operations.',
      image: 'https://images.pexels.com/photos/256381/pexels-photo-256381.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Custom IoT Device Integration',
        'Real-Time Data Collection & Analytics',
        'Edge & Cloud Computing Solutions',
        'Predictive Maintenance Systems',
        'IoT Security and Access Control',
        'Remote Monitoring & Automation'
      ],
      benefits: [
        'Increased operational efficiency',
        'Reduced maintenance costs with predictive insights',
        'Improved asset tracking and utilization',
        'Faster decision-making with real-time data',
        'Enhanced scalability and system interoperability',
        'Lower downtime and service disruption'
      ],
      technologies: ['MQTT', 'LoRaWAN', 'Edge Computing', 'Azure IoT Hub', 'InfluxDB', 'Grafana', 'Docker'],
      process: [
        { step: 'Discovery & Planning', description: 'Understand client needs and define IoT use cases' },
        { step: 'Architecture & Design', description: 'Design scalable and secure IoT infrastructure' },
        { step: 'Deployment & Integration', description: 'Install and integrate devices, gateways, and platforms' },
        { step: 'Monitoring & Optimization', description: 'Enable real-time monitoring, analytics, and iterative improvement' }
      ],
      caseStudies: [
        {
          title: 'Smart Factory Automation',
          client: 'Manufacturing Company',
          result: '30% increase in equipment uptime',
          description: 'Deployed IoT sensors and analytics to monitor and optimize factory floor operations.'
        },
        {
          title: 'Remote Energy Monitoring',
          client: 'Utility Provider',
          result: '20% reduction in operational costs',
          description: 'Implemented an IoT-based monitoring platform for real-time energy consumption tracking.'
        }
      ]
    },
    'Product Development': {
      icon: Zap,
      title: 'Digital Transformation',
      subtitle: 'Modernize Your Business for the Digital Age',
      description: 'Transform your business processes and culture to leverage digital technologies effectively. Our digital transformation services help you stay competitive and drive innovation across your organization.',
      image: 'https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=800',
      features: [
        'Process Automation',
        'Legacy System Modernization',
        'Digital Strategy Development',
        'Change Management',
        'Technology Integration',
        'Performance Optimization'
      ],
      benefits: [
        'Increased operational efficiency by 65%',
        'Improved customer experience',
        'Enhanced employee productivity',
        'Reduced operational costs',
        'Faster innovation cycles',
        'Better market responsiveness'
      ],
      technologies: ['RPA Tools', 'Low-code Platforms', 'API Management', 'Microservices', 'Cloud Platforms', 'Analytics Tools'],
      process: [
        { step: 'Digital Maturity Assessment', description: 'Evaluating current digital capabilities and readiness' },
        { step: 'Strategy Development', description: 'Creating comprehensive digital transformation roadmap' },
        { step: 'Implementation', description: 'Executing transformation initiatives with change management' },
        { step: 'Optimization', description: 'Continuous improvement and optimization of digital processes' }
      ],
      caseStudies: [
        {
          title: 'Retail Digital Transformation',
          client: 'Traditional Retailer',
          result: '200% increase in online revenue',
          description: 'Transformed traditional retail operations with omnichannel digital strategy.'
        },
        {
          title: 'Manufacturing Process Automation',
          client: 'Industrial Manufacturer',
          result: '50% reduction in production time',
          description: 'Automated manufacturing processes with IoT and AI integration.'
        }
      ]
    }
  };

  const service = serviceData[serviceId || ''];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-800">Return to Home</Link>
        </div>
      </div>
    );
  }

  const IconComponent = service.icon;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link 
              to="/#services" 
              className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-8 group"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Services
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center mb-6">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 rounded-2xl mr-4"
                >
                  <IconComponent className="w-8 h-8 text-white" />
                </motion.div>
                <div>
                  <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h1>
                  <p className="text-xl text-blue-600 font-medium">{service.subtitle}</p>
                </div>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                {service.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src={service.image}
                alt={service.title}
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features & Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Features */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Features</h2>
              <div className="space-y-4">
                {service.features.map((feature: string, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center"
                  >
                    <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Business Benefits</h2>
              <div className="space-y-4">
                {service.benefits.map((benefit: string, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-center"
                  >
                    <Star className="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Technologies We Use</h2>
            <p className="text-lg text-gray-600">Cutting-edge tools and frameworks for optimal results</p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {service.technologies.map((tech: string, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ scale: 1.05 }}
                className="bg-white px-6 py-3 rounded-full shadow-sm border border-gray-200 hover:border-blue-300 transition-colors"
              >
                <span className="text-gray-700 font-medium">{tech}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Process</h2>
            <p className="text-lg text-gray-600">A proven methodology for successful project delivery</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.process.map((step: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                  className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4"
                >
                  {index + 1}
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.step}</h3>
                <p className="text-gray-600">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Success Stories</h2>
            <p className="text-lg text-gray-600">Real results from our client partnerships</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {service.caseStudies.map((study: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -5 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <Users className="w-6 h-6 text-blue-600 mr-3" />
                  <span className="text-sm text-gray-500">{study.client}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{study.title}</h3>
                <p className="text-gray-600 mb-4">{study.description}</p>
                <div className="flex items-center">
                  <Award className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-green-600 font-semibold">{study.result}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Get Started with {service.title}?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Let's discuss how we can help transform your business with our {service.title.toLowerCase()} services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
              >
                Schedule Consultation
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                View Portfolio
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;